import React, { useEffect, useState } from 'react';
import { Box, Card, Skeleton, Switch, Typography } from '@mui/material';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../api/notifications.api';
import type { NotificationPreferences, UpdateNotificationPreferencesInput } from '../types';
import { useNotification } from '../../../hooks/useNotification';

interface NotificationPreferencesSectionProps {
  variant?: 'page' | 'modal';
}

type PreferenceKey = keyof NotificationPreferences;

interface PreferenceRowProps {
  checked: boolean;
  disabled: boolean;
  helper: string;
  label: string;
  onChange: (checked: boolean) => void;
}

const PreferenceRow: React.FC<PreferenceRowProps> = ({
  checked,
  disabled,
  helper,
  label,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Box className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <Box className="min-w-0">
        <Typography variant="subtitle2" className="font-semibold text-gray-900 dark:text-white">
          {label}
        </Typography>
        <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
          {helper}
        </Typography>
      </Box>
      <Box className="flex shrink-0 items-center gap-2 self-end sm:self-center">
        <Typography
          variant="body2"
          className="text-sm font-medium text-gray-700 dark:text-slate-300"
        >
          {checked ? t('common.enabled') : t('common.disabled')}
        </Typography>
        <Switch
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
          color="primary"
          inputProps={{ 'aria-label': label }}
        />
      </Box>
    </Box>
  );
};

export const NotificationPreferencesSection: React.FC<NotificationPreferencesSectionProps> = ({
  variant = 'page',
}) => {
  const { t } = useTranslation();
  const { error: notifyError } = useNotification();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState<PreferenceKey | null>(null);
  const isModal = variant === 'modal';
  const cardClassName = `${
    isModal ? 'w-full' : 'max-w-2xl'
  } mb-4 border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800`;

  useEffect(() => {
    let isMounted = true;

    const loadPreferences = async () => {
      setIsLoading(true);
      try {
        const data = await getNotificationPreferences();
        if (isMounted) {
          setPreferences(data);
        }
      } catch {
        if (isMounted) {
          notifyError(t('notifications.preferences.errors.loadFailed'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadPreferences();

    return () => {
      isMounted = false;
    };
  }, [notifyError, t]);

  const handleToggle = async (key: PreferenceKey, checked: boolean) => {
    if (!preferences || updatingKey) return;

    const previousPreferences = preferences;
    const nextPreferences = { ...preferences, [key]: checked };
    const input = { [key]: checked } as UpdateNotificationPreferencesInput;

    setPreferences(nextPreferences);
    setUpdatingKey(key);

    try {
      const updatedPreferences = await updateNotificationPreferences(input);
      setPreferences(updatedPreferences);
    } catch {
      setPreferences(previousPreferences);
      notifyError(t('notifications.preferences.errors.updateFailed'));
    } finally {
      setUpdatingKey(null);
    }
  };

  return (
    <Card className={cardClassName} elevation={0}>
      <Box className="mb-5 flex min-w-0 items-start gap-3">
        <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
          <Bell className="text-cyan-600 dark:text-cyan-400" size={20} />
        </Box>
        <Box className="min-w-0">
          <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-white">
            {t('notifications.preferences.title')}
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
            {t('notifications.preferences.description')}
          </Typography>
        </Box>
      </Box>

      {isLoading && !preferences ? (
        <Box className="space-y-4">
          {Array.from({ length: 2 }).map((_, index) => (
            <Box key={index} className="flex items-center justify-between gap-4">
              <Box className="flex-1">
                <Skeleton width="45%" height={24} />
                <Skeleton width="90%" height={20} />
              </Box>
              <Skeleton variant="rounded" width={58} height={32} />
            </Box>
          ))}
        </Box>
      ) : preferences ? (
        <Box className="space-y-4">
          <PreferenceRow
            label={t('notifications.preferences.emailMarketing.label')}
            helper={t('notifications.preferences.emailMarketing.helper')}
            checked={preferences.emailMarketing}
            disabled={isLoading || Boolean(updatingKey)}
            onChange={(checked) => void handleToggle('emailMarketing', checked)}
          />
          <PreferenceRow
            label={t('notifications.preferences.inAppMarketing.label')}
            helper={t('notifications.preferences.inAppMarketing.helper')}
            checked={preferences.inAppMarketing}
            disabled={isLoading || Boolean(updatingKey)}
            onChange={(checked) => void handleToggle('inAppMarketing', checked)}
          />
        </Box>
      ) : null}
    </Card>
  );
};
